import { useState, useEffect, useCallback } from 'react'
import { reminderService } from '@/services/reminderService'
import type { ReminderSettings, ReminderSettingsResponse, ReminderChannel } from '@weekly-report/shared-types'

interface UseReminderSettingsReturn {
  settings: ReminderSettings | null
  nextSendAt: string | null
  remindersEnabled: boolean
  loading: boolean
  error: Error | null
  saving: boolean
  saveError: Error | null
  updateSettings: (settings: Partial<ReminderSettings>) => Promise<ReminderSettingsResponse>
  remindNow: (channels?: ReminderChannel[]) => Promise<void>
  refetch: () => Promise<void>
}

/**
 * Hook for managing user reminder settings
 * 
 * Provides:
 * - Current reminder settings
 * - Next scheduled send time
 * - Whether global reminders are enabled
 * - Methods to update settings and send immediate reminders
 */
export const useReminderSettings = (): UseReminderSettingsReturn => {
  const [settings, setSettings] = useState<ReminderSettings | null>(null)
  const [nextSendAt, setNextSendAt] = useState<string | null>(null)
  const [remindersEnabled, setRemindersEnabled] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [saving, setSaving] = useState<boolean>(false)
  const [saveError, setSaveError] = useState<Error | null>(null)

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await reminderService.getSettings()
      
      setSettings(response.settings)
      setNextSendAt(response.nextSendAt)
      setRemindersEnabled(response.remindersEnabled)
    } catch (err) {
      console.error('[useReminderSettings] Error fetching settings:', err)
      setError(err instanceof Error ? err : new Error('Failed to load reminder settings'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const updateSettings = useCallback(async (newSettings: Partial<ReminderSettings>): Promise<ReminderSettingsResponse> => {
    setSaving(true)
    setSaveError(null)
    
    // Optimistic update
    const previousSettings = settings
    const previousNextSendAt = nextSendAt
    
    if (settings) {
      setSettings({
        ...settings,
        ...newSettings,
        updatedAt: new Date().toISOString()
      })
    }

    try {
      const response = await reminderService.updateSettings(newSettings)
      
      // Update with server response
      setSettings(response.settings)
      setNextSendAt(response.nextSendAt)
      setRemindersEnabled(response.remindersEnabled)
      
      return response
    } catch (err) {
      // Rollback on error
      setSettings(previousSettings)
      setNextSendAt(previousNextSendAt)
      
      const error = err instanceof Error ? err : new Error('Failed to save reminder settings')
      setSaveError(error)
      throw error
    } finally {
      setSaving(false)
    }
  }, [settings, nextSendAt])

  const remindNow = useCallback(async (channels?: ReminderChannel[]): Promise<void> => {
    try {
      await reminderService.remindNow(channels)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to send reminder')
      throw error
    }
  }, [])

  const refetch = useCallback(async (): Promise<void> => {
    await fetchSettings()
  }, [fetchSettings])

  return {
    settings,
    nextSendAt,
    remindersEnabled,
    loading,
    error,
    saving,
    saveError,
    updateSettings,
    remindNow,
    refetch
  }
}

